import { ApiError, StaffCapability, StaffMember, StaffService, StaffShift } from '@/api'
import PaneContainer from '@/layouts/components/PaneContainer'
import SingleColumn from '@/layouts/SingleColumn'
import { toastAtom } from '@/atoms/toastAtom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useAtomValue } from 'jotai'
import { useEffect, useState } from 'react'
import { Button } from 'primereact/button'
import StaffForm from './components/StaffForm'
import StaffList from './components/StaffList'

const emptyStaff = (): StaffMember => ({
  name: '',
  shift: StaffShift.DAY,
  can_handle_lidded: 'disable' as StaffCapability,
  can_handle_appearance: 'disable' as StaffCapability,
  can_handle_other: 'disable' as StaffCapability,
})

const Staff = () => {
  const toast = useAtomValue(toastAtom)
  const queryClient = useQueryClient()

  const [staff, setStaff] = useState<StaffMember[]>([])
  const [form, setForm] = useState<StaffMember>(emptyStaff())
  const [editingIndex, setEditingIndex] = useState<number | null>(null)

  const staffQuery = useQuery({
    queryKey: ['staff'],
    queryFn: () => StaffService.getStaff(),
    refetchOnWindowFocus: false,
  })

  useEffect(() => {
    if (staffQuery.data?.staff) {
      const normalized = staffQuery.data.staff.map((member) => ({
        name: member.name,
        shift: member["shift"] as StaffShift,
        can_handle_lidded: member.can_handle_lidded as StaffCapability,
        can_handle_appearance: member.can_handle_appearance as StaffCapability,
        can_handle_other: member.can_handle_other as StaffCapability,
      }))
      setStaff(normalized)
    }
  }, [staffQuery.dataUpdatedAt])

  const saveMutation = useMutation({
    mutationFn: (nextStaff: StaffMember[]) => {
        return StaffService.saveStaff({ staff: nextStaff } as any)
    },
    onSuccess: (res) => {
      const normalized = res.staff.map((member) => ({
        name: member.name,
        shift: member["shift"] as StaffShift,
        can_handle_lidded: member.can_handle_lidded as StaffCapability,
        can_handle_appearance: member.can_handle_appearance as StaffCapability,
        can_handle_other: member.can_handle_other as StaffCapability,
      })) satisfies StaffMember[]
      setStaff(normalized)
      queryClient.invalidateQueries({ queryKey: ['staff'] })
      toast?.show({
        severity: 'success',
        summary: '保存完了',
        detail: 'スタッフ情報を保存しました。',
        life: 2500,
      })
    },
    onError: (error: unknown) => {
      const detail = error instanceof ApiError
        ? `保存に失敗しました (status: ${error.status})`
        : '保存に失敗しました。'
      toast?.show({
        severity: 'error',
        summary: '保存エラー',
        detail,
        life: 3000,
      })
    },
  })

  const resetForm = () => {
    setForm(emptyStaff())
    setEditingIndex(null)
  }

  const handleUpsert = () => {
    const normalizedName = form.name.trim()
    if (!normalizedName) {
      toast?.show({
        severity: 'warn',
        summary: '入力不足',
        detail: '名前を入力してください。',
        life: 2500,
      })
      return
    }

    const nextMember: StaffMember = {
      ...form,
      name: normalizedName,
    }

    console.log("Upserting member:", nextMember)

    if (editingIndex === null) {
      setStaff((prev) => [...prev, nextMember])
    } else {
      setStaff((prev) => prev.map((member, index) => (index === editingIndex ? nextMember : member)))
    }

    resetForm()
  }

  const handleEdit = (index: number) => {
    setForm(staff[index])
    setEditingIndex(index)
  }

  const handleDelete = (index: number) => {
    setStaff((prev) => prev.filter((_, i) => i !== index))
    if (editingIndex === index) {
      resetForm()
    }
  }

  const handleMove = (index: number, direction: -1 | 1) => {
    setStaff((prev) => {
      const current = prev[index]
      if (!current) return prev

      const sameShiftIndices = prev
        .map((member, i) => ({ member, i }))
        .filter((entry) => entry.member.shift === current.shift)
        .map((entry) => entry.i)

      const currentPosition = sameShiftIndices.indexOf(index)
      if (currentPosition < 0) return prev

      const targetPosition = currentPosition + direction
      if (targetPosition < 0 || targetPosition >= sameShiftIndices.length) return prev

      const targetIndex = sameShiftIndices[targetPosition]
      const next = [...prev]
      ;[next[index], next[targetIndex]] = [next[targetIndex], next[index]]

      setEditingIndex((prevEditing) => {
        if (prevEditing === null) return prevEditing
        if (prevEditing === index) return targetIndex
        if (prevEditing === targetIndex) return index
        return prevEditing
      })

      return next
    })
  }

  const handleSave = () => {
    saveMutation.mutate(staff)
  }

  const isBusy = staffQuery.isLoading || saveMutation.isPending

  return (
    <SingleColumn>
      <PaneContainer>
        <div className="tm-grid tm-grid-cols-1 gap-4 w-full tw-mx-2 mx-auto p-2">
          <div>
            <Link to="/dashboard">&lt; ダッシュボードに戻る</Link>
          </div>

          <h1 className="text-2xl font-medium mb-0">スタッフ管理</h1>
          <p className="text-700 mt-0 mb-2">スタッフ情報を編集し、サーバーに保存します。</p>

          <div className="flex flex-column gap-3">
            <StaffList
              staff={staff}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onMove={handleMove}
              disabled={isBusy}
            />

            <StaffForm
              value={form}
              onChange={setForm}
              onSubmit={handleUpsert}
              onCancelEdit={resetForm}
              isEditing={editingIndex !== null}
              disabled={isBusy}
            />

            <div className="flex align-items-center gap-2">
              <Button
                label="サーバーへ保存"
                icon="pi pi-save"
                onClick={handleSave}
                loading={saveMutation.isPending}
                disabled={staffQuery.isLoading}
              />
              <Button
                label="再読み込み"
                icon="pi pi-refresh"
                outlined
                onClick={() => staffQuery.refetch()}
                disabled={isBusy}
              />
            </div>
          </div>
        </div>
      </PaneContainer>
    </SingleColumn>
  )
}

export default Staff
